import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { PageHeader, SectionCard, StatTile, Tabs } from "@/features/power_projects/components/ui";

import {
  CountBars,
  ExpectedValueChart,
  OutcomeBars,
  PriceHeadroomChart,
  WinRateLine,
  pct,
} from "../analytics/charts";
import { GroupTendersModal } from "../analytics/GroupTendersModal";
import {
  useAsk,
  useCancelled,
  useGenerateMonthly,
  useMonthly,
  useOverview,
  usePricingItems,
  usePricingItemWinrate,
} from "../analytics/hooks";
import type {
  AnalyticsFilters,
  AskResult,
  BarLabelConfig,
  LabelStat,
  WinRateGroup,
} from "../analytics/types";

type TabKey = "overview" | "cancelled" | "ai";
type Preset = "all" | "30" | "90" | "365";

type LabelMetricChoice = "none" | "value" | "multiplier";
const STAT_OPTIONS: { key: LabelStat; label: string }[] = [
  { key: "avg", label: "Średnia" },
  { key: "median", label: "Mediana" },
  { key: "p25", label: "P25" },
  { key: "p75", label: "P75" },
];

// Dimensions worth inspecting for pricing headroom (typical bid vs win-rate per segment).
const HEADROOM_DIMS: { key: string; label: string }[] = [
  { key: "route_length", label: "Długość trasy" },
  { key: "voltage", label: "Napięcie (nN / SN)" },
  { key: "value", label: "Wartość oferty" },
  { key: "branch", label: "Oddział" },
  { key: "rejon", label: "Rejon" },
  { key: "trafo", label: "Liczba stacji trafo" },
];

interface GroupModalTarget {
  dimension: string;
  category: string;
  title: string;
}

const money = (v: number | null | undefined) =>
  v == null ? "—" : `${new Intl.NumberFormat("pl-PL").format(Math.round(v))} zł`;
const asMult = (v: number | null | undefined) => (v == null ? "—" : `${v}%`);

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

const PRESETS: { key: Preset; label: string }[] = [
  { key: "all", label: "Wszystko" },
  { key: "30", label: "30 dni" },
  { key: "90", label: "90 dni" },
  { key: "365", label: "12 mies." },
];

const dim = (by: Record<string, WinRateGroup[]>, key: string): WinRateGroup[] => by[key] ?? [];

const QUICK_QUESTIONS = [
  "Które pozycje wyceny mamy zawyżone, a które zaniżone?",
  "Jaki mamy win-rate przy mnożniku powyżej 120%?",
  "Gdzie wygrywamy najczęściej — który klient i rejon?",
];

export function ZETenderAnalyticsPage() {
  const [tab, setTab] = useState<TabKey>("overview");
  const [preset, setPreset] = useState<Preset>("all");
  const [officialOnly, setOfficialOnly] = useState(false);
  const [costPct, setCostPct] = useState(75);
  const [headroomDim, setHeadroomDim] = useState("route_length");
  const [labelMetric, setLabelMetric] = useState<LabelMetricChoice>("none");
  const [labelStat, setLabelStat] = useState<LabelStat>("avg");
  const [modal, setModal] = useState<GroupModalTarget | null>(null);
  const [pricingItemKey, setPricingItemKey] = useState<string | null>(null);

  const labelConfig: BarLabelConfig | null =
    labelMetric === "none" ? null : { metric: labelMetric, stat: labelStat };

  const filters: AnalyticsFilters = useMemo(
    () => ({
      from: preset === "all" ? undefined : isoDaysAgo(Number(preset)),
      official_only: officialOnly,
    }),
    [preset, officialOnly],
  );

  const overview = useOverview(filters);
  const cancelled = useCancelled(filters);
  const pricingItems = usePricingItems(filters);
  const pricingItemWinrate = usePricingItemWinrate(pricingItemKey, filters);

  // Default the pricing-item selector to the most-frequent item once the catalog loads (and
  // recover if the current selection drops out of the filtered catalog).
  useEffect(() => {
    const items = pricingItems.data?.items;
    if (!items?.length) return;
    if (!pricingItemKey || !items.some((it) => it.key === pricingItemKey)) {
      setPricingItemKey(items[0].key);
    }
  }, [pricingItems.data, pricingItemKey]);

  const [generating, setGenerating] = useState(false);
  const prevGen = useRef<string | null | undefined>(undefined);
  const monthly = useMonthly(generating);
  const genMonthly = useGenerateMonthly();

  useEffect(() => {
    if (!generating) return;
    if ((monthly.data?.generated_at ?? null) !== prevGen.current) {
      setGenerating(false);
      return;
    }
    const t = setTimeout(() => setGenerating(false), 150_000);
    return () => clearTimeout(t);
  }, [generating, monthly.data?.generated_at]);

  const onGenerate = () => {
    prevGen.current = monthly.data?.generated_at ?? null;
    setGenerating(true);
    genMonthly.mutate();
  };

  const ask = useAsk();
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<{ q: string; res: AskResult }[]>([]);
  const runAsk = (q: string) => {
    const text = q.trim();
    if (!text) return;
    ask.mutate(
      { question: text, filters },
      { onSuccess: (res) => setHistory((h) => [{ q: text, res }, ...h]) },
    );
  };

  const kpis = overview.data?.kpis;
  const by = overview.data?.by_dimension ?? {};

  // A single outcome-bar card wired to the on-bar stat label + the drill-down modal.
  const dimCard = (
    dimKey: string,
    cardTitle: string,
    opts?: { ordered?: boolean; className?: string },
  ) => (
    <SectionCard title={cardTitle} className={opts?.className}>
      <OutcomeBars
        data={dim(by, dimKey)}
        ordered={opts?.ordered}
        labelConfig={labelConfig}
        onSelect={(category) => setModal({ dimension: dimKey, category, title: cardTitle })}
      />
    </SectionCard>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Przetargi ZE Analityka"
        subtitle="Wygrane / przegrane / anulowane — skuteczność ofertowa i wnioski AI"
        backTo="/ze-tenders"
        backLabel="Przetargi ZE"
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg border border-gray-200 p-1 dark:border-gray-700">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`rounded-md px-3 py-1 text-sm ${
                preset === p.key
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={officialOnly} onChange={(e) => setOfficialOnly(e.target.checked)} />
          Tylko oficjalne wygrane
        </label>

        <div className="ml-auto flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          <span className="text-xs uppercase tracking-wide text-gray-400">Etykieta słupka</span>
          <select
            value={labelMetric}
            onChange={(e) => setLabelMetric(e.target.value as LabelMetricChoice)}
            className="rounded-md border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-600"
          >
            <option value="none">Brak</option>
            <option value="value">Cena</option>
            <option value="multiplier">Mnożnik</option>
          </select>
          <select
            value={labelStat}
            onChange={(e) => setLabelStat(e.target.value as LabelStat)}
            disabled={labelMetric === "none"}
            className="rounded-md border border-gray-300 bg-transparent px-2 py-1 text-sm disabled:opacity-40 dark:border-gray-600"
          >
            {STAT_OPTIONS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Tabs<TabKey>
        tabs={[
          { key: "overview", label: "Przegląd" },
          { key: "cancelled", label: "Anulowane", count: cancelled.data?.count },
          { key: "ai", label: "Analiza AI" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "overview" && (
        <>
          {overview.isLoading && <Spinner />}
          {overview.isError && <div className="text-sm text-red-600">Nie udało się wczytać danych.</div>}
          {kpis && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <StatTile
                  label="Win-rate"
                  value={pct(kpis.win_rate)}
                  tone={kpis.win_rate >= 0.5 ? "ok" : "warn"}
                  hint={`${kpis.won} / ${kpis.resolved} rozstrzygniętych`}
                />
                <StatTile label="Rozstrzygnięte" value={kpis.resolved} />
                <StatTile
                  label="Śr. mnożnik wygranych"
                  value={asMult(kpis.avg_multiplier_won)}
                  tone="blue"
                  hint={`przegrane: ${asMult(kpis.avg_multiplier_lost)}`}
                />
                <StatTile label="Anulowane" value={kpis.cancelled} tone="warn" />
                <StatTile label="Pokrycie rysunku" value={pct(kpis.coverage_drawing)} hint={`rejon: ${pct(kpis.coverage_rejon)}`} />
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SectionCard
                  title="Wartość oczekiwana wg mnożnika"
                  action={
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      Koszty wewn.
                      <input
                        type="number"
                        value={costPct}
                        onChange={(e) => setCostPct(Number(e.target.value) || 0)}
                        className="w-16 rounded border border-gray-300 px-1.5 py-0.5 text-right dark:border-gray-600 dark:bg-gray-800"
                      />
                      %
                    </label>
                  }
                >
                  <ExpectedValueChart groups={dim(by, "multiplier")} costPct={costPct} />
                  <p className="mt-1 text-xs text-gray-500">
                    Wartość oczek. = win-rate × (mnożnik − koszty wewn.). Np. wygrywanie 1/10
                    przy 200% potrafi bić 50% przy 100% — szczyt słupka to mnożnik optymalny dla
                    zysku.
                  </p>
                </SectionCard>
                <SectionCard title="Trend win-rate w czasie">
                  <WinRateLine data={overview.data!.trend} />
                </SectionCard>

                <SectionCard
                  title="Zapas cenowy — próg wyjściowy vs skuteczność"
                  className="lg:col-span-2"
                  action={
                    <select
                      value={headroomDim}
                      onChange={(e) => setHeadroomDim(e.target.value)}
                      className="rounded-md border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-600"
                    >
                      {HEADROOM_DIMS.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  }
                >
                  <PriceHeadroomChart groups={dim(by, headroomDim)} />
                  <p className="mt-1 text-xs text-gray-500">
                    Oś X = typowy mnożnik, z jakim <b>startujemy</b> w segmencie (mediana ofert),
                    oś Y = win-rate, wielkość punktu = liczba przetargów. Punkt wysoko i po lewej
                    (<span className="text-green-600 dark:text-green-400">zielony</span>: wygrywamy dużo,
                    a licytujemy poniżej 100%) = <b>niedowartościowany segment z zapasem</b> — tam
                    warto podbijać mnożnik przyrostowo. To portfelowy obraz tego, co hint wyceny robi
                    dla pojedynczego przetargu.
                  </p>
                </SectionCard>

                <SectionCard
                  title="Win-rate wg ilości pozycji wyceny"
                  className="lg:col-span-2"
                  action={
                    <select
                      value={pricingItemKey ?? ""}
                      onChange={(e) => setPricingItemKey(e.target.value || null)}
                      disabled={!pricingItems.data?.items.length}
                      className="max-w-[24rem] truncate rounded-md border border-gray-300 bg-transparent px-2 py-1 text-sm dark:border-gray-600"
                    >
                      {!pricingItems.data?.items.length && <option value="">Brak pozycji</option>}
                      {pricingItems.data?.items.map((it) => (
                        <option key={it.key} value={it.key}>
                          {it.name} ({it.occurrences})
                        </option>
                      ))}
                    </select>
                  }
                >
                  {pricingItemWinrate.isLoading && <Spinner />}
                  {pricingItemWinrate.isError && (
                    <div className="text-sm text-red-600">Nie udało się wczytać danych pozycji.</div>
                  )}
                  {pricingItemWinrate.data && (
                    <>
                      <OutcomeBars
                        data={pricingItemWinrate.data.groups}
                        ordered
                        labelConfig={labelConfig}
                      />
                      <PricingItemStatsTable groups={pricingItemWinrate.data.groups} />
                    </>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Przetargi rozstrzygnięte pogrupowane wg <b>ilości</b> wybranej pozycji
                    {pricingItemWinrate.data?.unit ? ` (w ${pricingItemWinrate.data.unit})` : ""} —
                    dokładne wartości gdy jest ich mało, inaczej przedziały. Kubełek{" "}
                    <b>„0 (brak)"</b> to przetargi, które tej pozycji <b>nie zawierały</b> (porównanie
                    „z pozycją" vs „bez"). Zielona część słupka = udział wygranych. <b>Mediana i
                    średnia mnożnika</b> w tabeli poniżej pokazują, jaką ceną osiągnięto ten win-rate
                    — 100% wygranych przy medianie 75% to inna historia niż przy 120%. Liczba przy
                    nazwie pozycji = w ilu przetargach wystąpiła.
                  </p>
                </SectionCard>

                {dimCard("multiplier", "Wg mnożnika wyceny", { ordered: true })}
                {dimCard("value", "Wg wartości oferty", { ordered: true })}
                {dimCard("voltage", "Wg napięcia (nN / SN)", { ordered: true })}
                {dimCard("trafo", "Wg liczby stacji trafo", { ordered: true })}
                {dimCard("client", "Wg klienta")}
                {dimCard("branch", "Wg oddziału")}
                {dimCard("coordinator", "Wg koordynatora")}
                {dimCard("rejon", `Wg rejonu (pokrycie danych: ${pct(kpis.coverage_rejon)})`)}
                {dimCard("contract_scope", "Wg zakresu umowy")}
                {dimCard("route_length", "Wg długości trasy", { ordered: true })}
                {dimCard("action", "Montaż / demontaż", { ordered: true })}
                {dimCard("private_parcels", "Wg liczby działek prywatnych", { ordered: true })}
                {dimCard("parcels_per_km", "Działek / km", { ordered: true })}
                {dimCard("private_parcels_per_km", "Działek prywatnych / km", { ordered: true })}
                {dimCard("wpw_per_km", "WPW / km", { ordered: true })}
                {dimCard("private_budget", "Wg % budżetu na działki prywatne (przybliżone)", {
                  ordered: true,
                  className: "lg:col-span-2",
                })}
              </div>
            </>
          )}
        </>
      )}

      {tab === "cancelled" && (
        <>
          <SectionCard title="O anulowanych">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Anulowane = przegrane, ale znaczy więcej: byliśmy najtańsi lub jedyni, a
              przekroczyliśmy budżet zamawiającego. To sygnał kalibracji ceny — jeśli anulowane
              grupują się przy wysokich mnożnikach, mnożnik jest za agresywny dla tych segmentów.
            </p>
          </SectionCard>
          {cancelled.isLoading && <Spinner />}
          {cancelled.data && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatTile label="Anulowanych" value={cancelled.data.count} tone="warn" />
                <StatTile label="Śr. mnożnik anulowanych" value={asMult(cancelled.data.avg_multiplier)} tone="blue" />
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <SectionCard title="Rozkład mnożnika anulowanych">
                  <CountBars data={cancelled.data.by_multiplier_bucket} />
                </SectionCard>
                <SectionCard title="Anulowane wg klienta">
                  <CountBars data={cancelled.data.by_client} />
                </SectionCard>
                <SectionCard title="Anulowane wg rejonu">
                  <CountBars data={cancelled.data.by_rejon} />
                </SectionCard>
              </div>
              <SectionCard title="Lista anulowanych (od najwyższego mnożnika)">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-400">
                        <th className="py-2 pr-3">Przetarg</th>
                        <th className="py-2 pr-3">Klient</th>
                        <th className="py-2 pr-3">Mnożnik</th>
                        <th className="py-2 pr-3">Wartość</th>
                        <th className="py-2 pr-3">Rejon</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelled.data.items.map((it) => (
                        <tr key={it.id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="py-2 pr-3">{it.title}</td>
                          <td className="py-2 pr-3">{it.client_name ?? "—"}</td>
                          <td className="py-2 pr-3">{asMult(it.multiplier)}</td>
                          <td className="py-2 pr-3">{money(it.offer_value)}</td>
                          <td className="py-2 pr-3">{it.rejon ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </>
          )}
        </>
      )}

      {tab === "ai" && (
        <>
          <SectionCard
            title="Miesięczna analiza AI"
            action={
              <Button size="sm" onClick={onGenerate} loading={generating || genMonthly.isPending}>
                {generating ? "Generuję…" : "Generuj analizę"}
              </Button>
            }
          >
            {monthly.data?.result?.insight ? (
              <MonthlyInsightView report={monthly.data} />
            ) : (
              <p className="text-sm text-gray-500">
                Brak wygenerowanej analizy. Kliknij „Generuj analizę" — AI podsumuje ostatni
                miesiąc (wnioski, rekomendacje cenowe, szanse i ryzyka). Generacja idzie w tle.
              </p>
            )}
          </SectionCard>

          <SectionCard title="Zapytaj AI o dowolny segment lub pozycje wyceny">
            <p className="mb-2 text-xs text-gray-500">
              „Typ projektu" definiujesz słownie — AI zbuduje filtr po danych z rysunku i policzy
              win-rate. Umie też ocenić, które pozycje wyceny mamy zawyżone/zaniżone (na podstawie
              stawek w wygranych vs przegranych).
            </p>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {QUICK_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    setQuestion(q);
                    runAsk(q);
                  }}
                  className="rounded-full border border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                placeholder="Zadaj pytanie o skuteczność w wybranym segmencie…"
              />
              <div>
                <Button size="sm" onClick={() => runAsk(question)} loading={ask.isPending} disabled={!question.trim()}>
                  Zapytaj
                </Button>
              </div>
            </div>
            {history.length > 0 && (
              <div className="mt-3 space-y-3">
                <div className="text-xs font-semibold uppercase text-gray-400">
                  Historia rozmowy (ta sesja)
                </div>
                {history.map((h, hi) => (
                  <div key={hi} className="rounded-md border border-gray-200 p-2 dark:border-gray-700">
                    <div className="mb-1 text-xs font-medium text-gray-500">Ty: {h.q}</div>
                    <div className="whitespace-pre-wrap rounded bg-gray-50 p-2 text-sm dark:bg-gray-800">
                      {h.res.answer}
                    </div>
                    {h.res.cohorts.map((c, i) => (
                      <div key={i} className="mt-1 text-xs text-gray-500">
                        <span className="font-mono">{JSON.stringify(c.filter)}</span> → n={c.result.n} ·
                        win-rate {pct(c.result.win_rate)} · śr. mnożnik {asMult(c.result.avg_multiplier)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}

      {modal && (
        <GroupTendersModal
          dimension={modal.dimension}
          category={modal.category}
          title={modal.title}
          filters={filters}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// Per-quantity-bucket stats so win-rate is never read without the price that produced it:
// median + average multiplier (and median offer value) sit next to each bucket's win-rate.
function PricingItemStatsTable({ groups }: { groups: WinRateGroup[] }) {
  if (!groups.length) return null;
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-400">
            <th className="py-1.5 pr-3">Ilość</th>
            <th className="py-1.5 pr-3">Win-rate</th>
            <th className="py-1.5 pr-3">Mnożnik — mediana</th>
            <th className="py-1.5 pr-3">Mnożnik — średnia</th>
            <th className="py-1.5 pr-3">Mnożnik — p25</th>
            <th className="py-1.5 pr-3">Mnożnik — p75</th>
            <th className="py-1.5 pr-3">Wartość — mediana</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <tr key={g.category} className="border-t border-gray-100 dark:border-gray-800">
              <td className="py-1.5 pr-3 font-medium">{g.category}</td>
              <td className="py-1.5 pr-3">
                {pct(g.win_rate)}{" "}
                <span className="text-gray-400">
                  ({g.won}/{g.n}){g.low_sample ? " · mała próba" : ""}
                </span>
              </td>
              <td className="py-1.5 pr-3">{asMult(g.stats?.multiplier?.median)}</td>
              <td className="py-1.5 pr-3">{asMult(g.stats?.multiplier?.avg)}</td>
              <td className="py-1.5 pr-3">{asMult(g.stats?.multiplier?.p25)}</td>
              <td className="py-1.5 pr-3">{asMult(g.stats?.multiplier?.p75)}</td>
              <td className="py-1.5 pr-3">{money(g.stats?.value?.median)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlyInsightView({
  report,
}: {
  report: NonNullable<ReturnType<typeof useMonthly>["data"]>;
}) {
  const insight = report?.result?.insight;
  if (!insight) return null;
  const trendLabel = { up: "▲ rośnie", flat: "▬ stabilnie", down: "▼ spada" }[insight.trend];
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <span className="rounded bg-[var(--color-primary)] px-2 py-0.5 text-xs text-white">{trendLabel}</span>
        {report?.generated_at && (
          <span className="text-xs text-gray-400">
            wygenerowano: {new Date(report.generated_at).toLocaleString("pl-PL")}
          </span>
        )}
      </div>
      <p className="font-medium">{insight.headline}</p>
      <InsightList title="Kluczowe wnioski" items={insight.key_findings} />
      <InsightList title="Rekomendacje cenowe" items={insight.pricing_recommendations} />
      <InsightList title="Szanse segmentowe" items={insight.segment_opportunities} />
      <InsightList title="Sygnały ryzyka" items={insight.risk_flags} />
      {insight.cancelled_note && (
        <div>
          <div className="text-xs font-semibold uppercase text-gray-400">Anulowane</div>
          <p>{insight.cancelled_note}</p>
        </div>
      )}
    </div>
  );
}

function InsightList({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-gray-400">{title}</div>
      <ul className="list-disc space-y-0.5 pl-5">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}
